import { useState, useEffect, useMemo, useRef, memo } from 'react';

import { RightOutlined } from '@ant-design/icons';
import QuotedPrice from '@lcsc-ui/components/QuotedPrice';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';

import useProductStore from '@/store/product';

function PriceListPage({ data }) {
  const [quotedPriceData, setQuotedPriceData] = useState({
    open: false,
    product: {},
  });

  const isOversea = data?.isOversea;
  const productRecord = data?.productRecord;

  const { productStockPriceData } = useProductStore();

  const addPoint = () => {
    buriedPoint({
      cp: 'product_detail',
      ty: 'click',
      op: 'Immediately_check',
      data: {
        PC: productRecord.productCode,
      },
    });
  };

  async function openQuotePriceModal() {
    const productPriceList = productStockPriceData?.productRecord?.productPriceList;
    const lastIndex = productPriceList?.length - 1;
    const numerousBargain = productPriceList[lastIndex]?.epNumber + 1;
    setQuotedPriceData({
      open: true,
      product: {
        productCode: productRecord?.productCode,
        expectedPrice: productPriceList[lastIndex]?.thePrice,
        specialNumber: numerousBargain * productRecord?.theRatio,
        productStock: productRecord?.validStockNumber * productRecord?.theRatio,
        productStep: productRecord?.theRatio,
        productUnit: productRecord?.productUnit,
        productMinEncapsulationUnit: productRecord?.productMinEncapsulationUnit,
        productMinEncapsulationNumber: productRecord?.productMinEncapsulationNumber,
      },
    });
  }

  function closeQuotePriceModal() {
    setQuotedPriceData({
      ...quotedPriceData,
      open: false,
    });
  }

  const PriceTableDomRenderer = ({ headers, rows, isShowDiscount }) => {
    return (
      <div className="mt-[8px] w-[330px]">
        <div className="flex h-[33px] text-[14px] px-[16px] text-[#69788A] w-[100%] rounded-t-[10px] bg-[#F1F1F1] text-left leading-[33px]">
          {headers.map((item, index) => (
            <span key={index} className="flex-1">
              {item}
            </span>
          ))}
        </div>
        <div className="bg-[#FAFAFA] rounded-b-[10px]">
          {rows?.map((item, index) => (
            <div
              key={index}
              className="flex px-[16px] h-[28px] text-left w-[330px] rounded-b-[10px] text-[14px] leading-[28px]"
            >
              {item.map((item, i) => (
                <span
                  key={i}
                  className="flex-1"
                  style={{
                    color:
                      item === '报价'
                        ? '#0093E6'
                        : isShowDiscount && i === 1
                          ? '#FF4960'
                          : isShowDiscount && i === 2
                            ? '#BFBFBF'
                            : '#000000',
                    textDecoration: isShowDiscount && i === 2 ? 'line-through' : 'none',
                  }}
                >
                  {item === '报价' ? <button onClick={openQuotePriceModal}>报价</button> : item}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const PriceTableCanvasRenderer = ({ headers, rows, isShowDiscount }) => {
    const canvasRef = useRef(null);

    // 绘制圆角矩形
    function drawRoundedRect(ctx, x, y, width, height, radii) {
      // 处理不同格式的半径参数
      let topLeft, topRight, bottomRight, bottomLeft;

      if (typeof radii === 'number') {
        // 如果是单个数字，所有角使用相同半径
        topLeft = topRight = bottomRight = bottomLeft = radii;
      } else if (Array.isArray(radii)) {
        // 如果是数组，按顺序获取各角半径
        topLeft = radii[0] || 0;
        topRight = radii[1] || 0;
        bottomRight = radii[2] || 0;
        bottomLeft = radii[3] || 0;
      } else {
        // 默认所有角为0（直角）
        topLeft = topRight = bottomRight = bottomLeft = 0;
      }

      ctx.beginPath();
      ctx.moveTo(x + topLeft, y);

      // 顶边
      ctx.lineTo(x + width - topRight, y);
      // 右上角
      ctx.quadraticCurveTo(x + width, y, x + width, y + topRight);
      // 右边
      ctx.lineTo(x + width, y + height - bottomRight);
      // 右下角
      ctx.quadraticCurveTo(x + width, y + height, x + width - bottomRight, y + height);
      // 底边
      ctx.lineTo(x + bottomLeft, y + height);
      // 左下角
      ctx.quadraticCurveTo(x, y + height, x, y + height - bottomLeft);
      // 左边
      ctx.lineTo(x, y + topLeft);
      // 左上角
      ctx.quadraticCurveTo(x, y, x + topLeft, y);
      ctx.closePath();
    }

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const dpr = 2;
      const headerHeight = 33;
      const headerPaddingX = 16;
      const rowHeight = 28;
      const canvasWidth = 330;
      const canvasHeight = headerHeight + (rows?.length || 0) * rowHeight;

      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;

      ctx.scale(dpr, dpr); // 缩放上下文

      // 清空画布
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // 绘制 Header 背景
      ctx.fillStyle = '#F1F1F1';
      ctx.beginPath();
      drawRoundedRect(ctx, 0, 0, canvasWidth, headerHeight, [10, 10, 0, 0]);
      ctx.fill();

      // 绘制 Header
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      const headerXPositions = [];
      let currentX = 16;
      headers.forEach((header) => {
        const columnWidth = (canvasWidth - headerPaddingX * 2) / headers.length;
        headerXPositions.push(currentX);
        ctx.fillStyle = '#69788A';
        ctx.fillText(header, currentX, headerHeight / 2 + 2);
        currentX += columnWidth;
      });

      // 绘制 Rows 背景
      ctx.fillStyle = '#FAFAFA';
      ctx.beginPath();
      drawRoundedRect(ctx, 0, headerHeight, canvasWidth, canvasHeight - headerHeight, [0, 0, 10, 10]);
      ctx.fill();

      // 绘制 Rows
      if (rows) {
        rows.forEach((row, rowIndex) => {
          const y = headerHeight + 14 + rowIndex * rowHeight;

          row.forEach((cell, cellIndex) => {
            const x = headerXPositions[cellIndex];
            const isDiscount = isShowDiscount && cellIndex === 1;
            const isOrigin = isShowDiscount && cellIndex === 2;

            if (isDiscount) {
              ctx.fillStyle = '#FF4960';
            } else if (isOrigin) {
              ctx.fillStyle = '#BFBFBF';
            } else if (cell === '报价') {
              ctx.fillStyle = '#0093E6';
            } else {
              ctx.fillStyle = '#000000';
            }

            ctx.fillText(cell, x, y);

            // 添加删除线
            if (isOrigin) {
              const textWidth = ctx.measureText(cell).width;
              ctx.strokeStyle = '#BFBFBF';
              ctx.beginPath();
              ctx.moveTo(x, y);
              ctx.lineTo(x + textWidth, y);
              ctx.stroke();
            }
          });
        });
      }

      if (productRecord?.isShowQuotedPriceBtn) {
        // 给报价按钮添加点击事件
        const quoteButton = {
          x: headerXPositions[1],
          y: headerHeight + rowHeight * (rows?.length - 1),
          width: 30,
          height: 28,
          isHovered: false,
        };

        const mousemoveHandler = (e) => {
          const rect = canvas.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;
          const isInside =
            mouseX >= quoteButton.x &&
            mouseX <= quoteButton.x + quoteButton.width &&
            mouseY >= quoteButton.y &&
            mouseY <= quoteButton.y + quoteButton.height;

          quoteButton.isHovered = isInside;
          canvas.style.cursor = isInside ? 'pointer' : 'default';
        };

        const clickHandler = () => {
          if (quoteButton.isHovered) {
            openQuotePriceModal();
          }
        };

        canvas.addEventListener('mousemove', mousemoveHandler);
        canvas.addEventListener('click', clickHandler);

        return () => {
          canvas.removeEventListener('mousemove', mousemoveHandler);
          canvas.removeEventListener('click', clickHandler);
        };
      }
    }, [headers, rows, isShowDiscount]);

    return (
      <div className="mt-[8px]">
        <canvas ref={canvasRef} />
      </div>
    );
  };

  const PriceContent = () => {
    if (!productStockPriceData.isFetched) return <></>;

    try {
      const isShowDiscount = productStockPriceData?.productRecord?.productPriceDiscountList?.length > 0;
      const discount = data?.isPlusDiscount ? 'PLUS价格' : '折扣价';
      const unit = `折合1${productRecord?.productMinEncapsulationUnit || productRecord?.unit}`;
      const productPriceList = productStockPriceData?.productRecord?.productPriceList || [];
      const productPriceDiscountList = productStockPriceData?.productRecord?.productPriceDiscountList?.[0]?.priceList;
      const headers = ['梯度', isShowDiscount ? discount : '售价', isShowDiscount ? '原价' : null, unit].filter(
        (item) => item !== null
      );
      const rows = productPriceList?.map((item, index) => {
        // 梯度
        const rank = `${Number(item?.spNumber) * Number(productRecord?.theRatio)}+`;
        // 折扣价
        const discountPrice = `￥${productPriceDiscountList?.[index]?.price || ''}`;
        // 售价
        const salePrice = `￥${item?.thePrice}`;
        // 原价
        const originPrice = productPriceDiscountList?.[index]?.price !== item?.thePrice ? salePrice : '';
        // 折合价
        const minEncapsulationUnitPrice = item.showMessageFlag ? `￥${item?.minEncapsulationUnitPrice}` : '';

        // 报价
        if (productRecord?.isShowQuotedPriceBtn && index + 1 === productPriceList?.length) {
          return [
            `${productPriceList[productPriceList.length - 1]?.epNumber + 1 * Number(productRecord?.theRatio)}+`,
            '报价',
            isShowDiscount ? '' : null,
            '',
          ].filter((item) => item !== null);
        }

        return [
          rank,
          isShowDiscount ? discountPrice : salePrice,
          isShowDiscount ? originPrice : null,
          minEncapsulationUnitPrice,
        ].filter((item) => item !== null);
      });

      return (
        <>
          <PriceTableDomRenderer headers={headers} rows={rows} isShowDiscount={isShowDiscount} />
        </>
      );
    } catch (error) {
      console.error('渲染价格表失败:', error);
      return <></>;
    }
  };

  const OverseaPriceContent = () => {
    const isShowDiscount = productRecord?.discountTieredPrices?.length > 0;
    const productPriceDiscountList = productRecord?.discountTieredPrices;
    const headers = useMemo(() => {
      return ['梯度', isShowDiscount ? '折扣价' : '售价', isShowDiscount ? '原价' : null].filter(
        (item) => item !== null
      );
    }, [isShowDiscount]);
    const rows = useMemo(() => {
      return productRecord?.tieredPrices?.map((item, index) => {
        // 梯度
        const rank = `${item?.countStart}+`;
        // 折扣价
        const discountPrice = `￥${productPriceDiscountList?.[index]?.discountPrice || ''}`;
        // 售价
        const salePrice = `￥${item?.price}`;
        // 原价
        const originPrice = productPriceDiscountList?.[index]?.discountPrice !== item?.price ? salePrice : '';

        return [rank, isShowDiscount ? discountPrice : salePrice, isShowDiscount ? originPrice : null].filter(
          (item) => item !== null
        );
      });
    }, [isShowDiscount, productPriceDiscountList]);

    return (
      <>
        <PriceTableDomRenderer headers={headers} rows={rows} />
      </>
    );
  };

  return (
    <>
      <div className="flex justify-between">
        <h3 className="text-[14px] font-bold not-italic text-[#020202]">最新价格</h3>
        {data?.isShowSmtSubsidyInfo && (
          <p className="text-[12px] text-[#54657D]">
            嘉立创贴片惊喜价格
            <a
              id="item-gojlcsmt"
              rel="nofollow"
              target="_blank"
              className="text-[#0093E6] ml-[5px]"
              data-pc={productRecord.productCode}
              onClick={addPoint}
              href={`https://www.jlcsmt.com/lcsc/detail?componentCode=${productRecord.productCode}&stockNumber=10&presaleOrderSource=shop`}
              ignore-spm
            >
              立即查看 <RightOutlined />
            </a>
          </p>
        )}
      </div>
      {productRecord?.isShowPrice === false ? (
        <div className="mt-[8px] text-[14px]">需询价</div>
      ) : isOversea ? (
        <OverseaPriceContent />
      ) : (
        <PriceContent />
      )}
      <QuotedPrice
        open={quotedPriceData?.open}
        product={quotedPriceData?.product}
        onCancel={() => closeQuotePriceModal()}
      />
    </>
  );
}

export default memo(PriceListPage);
